import React, { useState, useEffect } from 'react';
import { Upload, File, X, Download, Trash2, Image as ImageIcon, FileText, Search } from 'lucide-react';
import api from '../services/api';
import { convertHeicToJpeg, isHeicFile } from '../utils/convertHeic';

const ProjectFiles = ({ projectId, canUpload = true, shareToken = null }) => {
  const [files, setFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [description, setDescription] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (projectId) {
      loadFiles();
    }
  }, [projectId]);

  const loadFiles = async () => {
    try {
      setLoading(true);
      const response = await api.projectsAPI.getFiles(projectId, shareToken);
      const filesList = response.files || [];
      setFiles(filesList);
      setFilteredFiles(filesList);
    } catch (error) {
      console.error('Error loading files:', error);
      setFiles([]);
      setFilteredFiles([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter files based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredFiles(files);
    } else {
      const searchLower = searchTerm.toLowerCase();
      const filtered = files.filter(file => 
        file.file_name?.toLowerCase().includes(searchLower) ||
        file.description?.toLowerCase().includes(searchLower)
      );
      setFilteredFiles(filtered);
    }
  }, [searchTerm, files]);

  const handleFileSelect = async (e) => {
    let file = e.target.files[0];
    if (file) {
      if (isHeicFile(file)) {
        try {
          file = await convertHeicToJpeg(file);
        } catch (err) {
          alert(err.message || 'Could not convert HEIC image. Please try a JPG or PNG.');
          return;
        }
      }
      setSelectedFile(file);
      setShowUpload(true);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (description.trim()) {
        formData.append('description', description.trim());
      }

      await api.projectsAPI.uploadFile(projectId, formData);
      
      // Reset form
      setSelectedFile(null);
      setDescription('');
      setShowUpload(false);
      
      // Reload files
      loadFiles();
    } catch (error) {
      console.error('Error uploading file:', error);
      alert(`Failed to upload file: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId, fileName) => {
    if (!window.confirm(`Are you sure you want to delete "${fileName}"?`)) {
      return;
    }

    try {
      await api.projectsAPI.deleteFile(projectId, fileId);
      loadFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      alert(`Failed to delete file: ${error.message}`);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType?.startsWith('image/')) {
      return <ImageIcon className="w-5 h-5" />;
    }
    return <FileText className="w-5 h-5" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search Section */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search files by name or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
        />
      </div>

      {/* Upload Section */}
      {canUpload && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
          {!showUpload ? (
            <div className="text-center">
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <label className="cursor-pointer">
                <span className="text-green-600 hover:text-green-700 font-medium">
                  Upload a file
                </span>
                <input
                  type="file"
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </label>
              <p className="text-xs text-gray-500 mt-1">
                PDF, images, documents up to 4MB
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <File className="w-5 h-5 text-gray-400" />
                  <span className="text-sm font-medium">{selectedFile?.name}</span>
                  <span className="text-xs text-gray-500">
                    ({formatFileSize(selectedFile?.size)})
                  </span>
                </div>
                <button
                  onClick={() => {
                    setSelectedFile(null);
                    setShowUpload(false);
                    setDescription('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <input
                type="text"
                placeholder="Optional description..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
                <button
                  onClick={() => {
                    setSelectedFile(null);
                    setShowUpload(false);
                    setDescription('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Files List */}
      {filteredFiles.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <File className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <p>{searchTerm ? 'No files match your search' : 'No files uploaded yet'}</p>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="mt-2 text-sm text-green-600 hover:text-green-700"
            >
              Clear search
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredFiles.map((file) => (
            <div
              key={file.id}
              className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="text-gray-400 flex-shrink-0">
                  {getFileIcon(file.file_type)}
                </div>
                <div className="flex-1 min-w-0">
                  <a
                    href={file.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-gray-900 hover:text-green-600 truncate block"
                  >
                    {file.file_name}
                  </a>
                  {file.description && (
                    <p className="text-xs text-gray-500 mt-0.5">{file.description}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-0.5">
                    {formatFileSize(file.file_size)} • {formatDate(file.created_at)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a
                  href={file.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-gray-400 hover:text-green-600"
                  title="Download"
                >
                  <Download className="w-4 h-4" />
                </a>
                {canUpload && (
                  <button
                    onClick={() => handleDelete(file.id, file.file_name)}
                    className="p-2 text-gray-400 hover:text-red-600"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectFiles;





